import React, { useEffect, useState } from 'react';
import { X, ExternalLink, AlertTriangle } from 'lucide-react';
import { youtubeEmbedUrl, normalizeYouTubeId } from '../../lib/youtubeUtils';

interface VideoLightboxProps {
  videoId: string;
  title?: string;
  onClose: () => void;
}

/** Lightbox vidéo : l'iframe YouTube n'est montée que lorsque ce composant est rendu (au clic Play). */
const VideoLightbox: React.FC<VideoLightboxProps> = ({ videoId, title, onClose }) => {
  const id = normalizeYouTubeId(videoId);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/90 p-4" onClick={onClose}>
      <button className="absolute right-4 top-4 text-white/80 hover:text-white" onClick={onClose} aria-label="Fermer"><X size={28} /></button>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        {!id ? (
          <div className="rounded-xl bg-neutral-900 p-10 text-center text-white">
            <AlertTriangle size={32} className="mx-auto mb-3 text-amber-400" />
            <p className="font-bold">Vidéo indisponible</p>
            <p className="mt-1 text-sm text-white/60">Le lien de cette vidéo est invalide ou a été retiré.</p>
          </div>
        ) : (
          <>
            <div className="relative w-full overflow-hidden rounded-xl bg-black" style={{ paddingTop: '56.25%' }}>
              <iframe
                className="absolute inset-0 h-full w-full"
                src={youtubeEmbedUrl(id)}
                title={title || 'Vidéo'}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
                onError={() => setFailed(true)}
              />
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              {title && <p className="text-sm text-white/70">{title}</p>}
              <a href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white">
                <ExternalLink size={13} /> Ouvrir sur YouTube
              </a>
            </div>
            {failed && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
                <AlertTriangle size={13} /> La vidéo met du temps à charger ? Ouvrez-la directement sur YouTube.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VideoLightbox;
